import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { downloadMedia } from '@/lib/contentStudio';
import {
  listZCreations,
  deleteZCreation,
  toggleZFavorite,
  Z_MEDIA_KIND_META,
  type ZStudioCreation,
} from '@/lib/zstudio';

type Filter = 'all' | 'image' | 'video' | 'text' | 'ad' | 'campaign' | 'favorite';

const FILTERS: { key: Filter; label: string; icon: string }[] = [
  { key: 'all', label: 'Tout', icon: 'ri-apps-2-line' },
  { key: 'image', label: 'Images', icon: 'ri-image-line' },
  { key: 'video', label: 'Vidéos', icon: 'ri-film-line' },
  { key: 'text', label: 'Textes', icon: 'ri-file-text-line' },
  { key: 'ad', label: 'Publicités', icon: 'ri-megaphone-line' },
  { key: 'campaign', label: 'Campagnes', icon: 'ri-rocket-line' },
  { key: 'favorite', label: 'Favoris', icon: 'ri-star-line' },
];

function copyText(text: string) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

export default function ZStudioCreationsPage() {
  const { user } = useAuth();
  const [creations, setCreations] = useState<ZStudioCreation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [preview, setPreview] = useState<ZStudioCreation | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      setCreations(await listZCreations(user.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => creations.filter((c) => {
    if (filter === 'all') return true;
    if (filter === 'favorite') return c.favorite;
    if (filter === 'ad' || filter === 'campaign') return c.kind === filter;
    return c.media_kind === filter;
  }), [creations, filter]);

  const handleFavorite = async (c: ZStudioCreation) => {
    const next = !c.favorite;
    setCreations((prev) => prev.map((x) => (x.id === c.id ? { ...x, favorite: next } : x)));
    try { await toggleZFavorite(c.id, next); } catch { setCreations((prev) => prev.map((x) => (x.id === c.id ? { ...x, favorite: !next } : x))); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette création ?')) return;
    setCreations((prev) => prev.filter((x) => x.id !== id));
    try { await deleteZCreation(id); } catch { load(); }
  };

  if (!user?.id) {
    return <div className="flex items-center justify-center py-24"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <Link to="/dashboard/zstudio" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-4 cursor-pointer">
        <i className="ri-arrow-left-line"></i>ZStudio
      </Link>

      <div className="mb-5">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-archive-2-line text-accent-500"></i>Mes créations
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Toutes vos images, vidéos, textes et campagnes générés par ZStudio.</p>
      </div>

      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${filter === f.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            <i className={f.icon}></i>{f.label}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-dashed border-background-300/70 rounded-xl text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-magic-line text-2xl text-foreground-400"></i>
          </span>
          <p className="text-sm font-medium text-foreground-700">Aucune création dans ce filtre</p>
          <Link to="/dashboard/zstudio/wizard" className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i>Créer maintenant
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((c) => {
            const meta = Z_MEDIA_KIND_META[c.media_kind || ''] || Z_MEDIA_KIND_META.text;
            return (
              <div key={c.id} className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden flex flex-col">
                <button type="button" onClick={() => setPreview(c)} className="aspect-square bg-background-100 flex items-center justify-center overflow-hidden cursor-pointer">
                  {c.thumbnail_url || (c.media_kind === 'image' && c.url) ? (
                    <img src={c.thumbnail_url || (c.url as string)} alt={c.title || 'Création'} className="w-full h-full object-cover object-top" />
                  ) : c.media_kind === 'video' && c.url ? (
                    <video src={c.url} muted className="w-full h-full object-cover object-top" />
                  ) : (
                    <i className={`${meta.icon} text-3xl text-foreground-400`}></i>
                  )}
                </button>
                <div className="p-2.5 flex-1 flex flex-col">
                  <p className="text-xs font-medium text-foreground-800 truncate">{c.title || 'Sans titre'}</p>
                  <span className={`inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-full w-fit ${meta.className}`}>
                    <i className={meta.icon}></i>{meta.label}
                  </span>
                  <div className="flex items-center gap-0.5 mt-2 pt-2 border-t border-background-200/70">
                    <button type="button" onClick={() => handleFavorite(c)} className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${c.favorite ? 'text-accent-600' : 'text-foreground-400 hover:text-accent-600'}`} title="Favori">
                      <i className={c.favorite ? 'ri-star-fill' : 'ri-star-line'}></i>
                    </button>
                    {c.media_kind === 'text' && c.url && (
                      <button type="button" onClick={() => copyText(c.url as string)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Copier">
                        <i className="ri-file-copy-line"></i>
                      </button>
                    )}
                    {c.media_kind !== 'text' && c.url && (
                      <button type="button" onClick={() => setPreview(c)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Aperçu">
                        <i className="ri-zoom-in-line"></i>
                      </button>
                    )}
                    {c.media_kind === 'video' && c.url && (
                      <button type="button" onClick={() => downloadMedia(c.url as string, `${c.title || 'zstudio'}.mp4`)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Télécharger">
                        <i className="ri-download-2-line"></i>
                      </button>
                    )}
                    {c.media_kind === 'image' && c.url && (
                      <button type="button" onClick={() => downloadMedia(c.url as string, `${c.title || 'zstudio'}.png`)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Télécharger">
                        <i className="ri-download-2-line"></i>
                      </button>
                    )}
                    <button type="button" onClick={() => handleDelete(c.id)} className="w-7 h-7 ml-auto flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 transition-colors cursor-pointer" title="Supprimer">
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Aperçu */}
      {preview && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setPreview(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="w-full max-w-3xl bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden max-h-[90vh] flex flex-col pointer-events-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 truncate">{preview.title || 'Création'}</h3>
                <div className="flex items-center gap-1">
                  {(preview.url) && (
                    <button type="button" onClick={() => downloadMedia(preview.url as string, `${preview.title || 'zstudio'}.${preview.media_kind === 'video' ? 'mp4' : 'png'}`)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-600 hover:bg-background-100 transition-colors cursor-pointer" title="Télécharger"><i className="ri-download-2-line"></i></button>
                  )}
                  <button type="button" onClick={() => setPreview(null)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-close-line"></i></button>
                </div>
              </div>
              <div className="p-4 overflow-y-auto">
                {preview.media_kind === 'video' && preview.url ? (
                  <video src={preview.url} controls className="w-full rounded-lg bg-black object-contain max-h-[70vh]" />
                ) : preview.media_kind === 'image' && preview.url ? (
                  <img src={preview.url} alt={preview.title || ''} className="w-full rounded-lg object-contain max-h-[70vh] mx-auto" />
                ) : (
                  <div className="bg-background-100 rounded-lg p-4 text-sm text-foreground-700 whitespace-pre-wrap break-words">{preview.url || preview.description || 'Aucun contenu texte.'}</div>
                )}
                {preview.content && typeof preview.content === 'object' && (
                  <div className="mt-3 space-y-2">
                    {Object.entries(preview.content).map(([key, value]) => {
                      if (value == null || value === '') return null;
                      const shown = Array.isArray(value) ? value.join(' ') : String(value);
                      return (
                        <div key={key} className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] uppercase tracking-wide text-foreground-400">{key}</p>
                            <p className="text-xs text-foreground-700 whitespace-pre-wrap break-words">{shown}</p>
                          </div>
                          <button type="button" onClick={() => copyText(shown)} className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Copier">
                            <i className="ri-file-copy-line"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}